import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  QaiResultsTabComponent,
  QaiSectionHeaderComponent,
  QaiInfoBlockComponent,
  QaiLabelComponent,
  type SectionHeaderIndicator,
  type InfoBlockIcon,
} from '@qai/ui';
import { IFlattenedMessageAdherenceSection } from 'src/app/interfaces/iflattenedMessageAdherenceSection';
import { IScoreMessageAdherence } from 'src/app/interfaces/iscore-message-adherence';
import { IScoreMessageAdherenceFact } from 'src/app/interfaces/imessage-adherence-fact';
import { getTabScoreKind } from 'src/app/utils/score-kind.util';

@Component({
  selector: 'app-result-messaging',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    QaiResultsTabComponent,
    QaiSectionHeaderComponent,
    QaiInfoBlockComponent,
    QaiLabelComponent,
  ],
  templateUrl: './result-messaging.component.html',
  styleUrls: ['./result-messaging.component.scss'],
})
export class ResultMessagingComponent {
  @Input() messageAdherences: IScoreMessageAdherence[] = [];
  @Input() flattenedSections: IFlattenedMessageAdherenceSection[] = [];
  @Input() isMessageAdherenceV2: boolean = false;
  @Input() overallScore: string = '';
  @Input() scoreCriteria: number = 0;

  get checkIcon(): InfoBlockIcon {
    return { type: 'img', src: '/assets/icons/check-2.svg', alt: this.translocoService.translate('CHECKMARK_ICON_ALT', {}, 'results') };
  }

  get crossIcon(): InfoBlockIcon {
    return { type: 'img', src: '/assets/icons/x-error.svg', alt: this.translocoService.translate('X_ICON_ALT', {}, 'results') };
  }

  get scoreKind(): 'success' | 'warning' | 'error' {
    if (!this.overallScore) return 'success';
    return getTabScoreKind(parseFloat(this.overallScore), this.scoreCriteria);
  }

  constructor(private translocoService: TranslocoService) {}

  getScoreIndicator(score: number): SectionHeaderIndicator {
    if (score === 1) {
      return {
        type: 'text',
        kind: 'error',
        label: this.translocoService.translate('NO_CREDIT', {}, 'results'),
      };
    }
    if (score === 2) {
      return {
        type: 'text',
        kind: 'warning',
        label: this.translocoService.translate('PARTIAL_CREDIT', {}, 'results'),
      };
    }
    return {
      type: 'text',
      kind: 'secondary-brand',
      label: this.translocoService.translate('FULL_CREDIT', {}, 'results'),
    };
  }

  getV2ScoreIndicator(
    numIncludedCorrectly: number,
    totalNum: number,
  ): SectionHeaderIndicator {
    return {
      type: 'text',
      kind: 'secondary-brand',
      label: `${numIncludedCorrectly}/${totalNum}`,
    };
  }

  getV1PositiveItems(section: IScoreMessageAdherence): string[] {
    if (section.positive) return [section.positive.toString()];
    if (!section.positive && section.negative) {
      return [
        this.translocoService.translate('MISSED_ALL_KEY_POINTS', {}, 'results'),
      ];
    }
    return [];
  }

  getV1NegativeItems(section: IScoreMessageAdherence): string[] {
    if (section.negative) return [section.negative];
    if (section.positive && !section.negative) {
      return [
        this.translocoService.translate('PERFECT_SCORE_MESSAGE', {}, 'results'),
      ];
    }
    return [];
  }

  getFactTexts(facts: IScoreMessageAdherenceFact[]): string[] {
    return facts.map((f) => f.factText || f.messageText || '').filter(Boolean);
  }
}
